import { useCallback, useEffect, useState } from "react";
import { packages as packagesService } from "../../../api/packages.service";
import { SITES, LANGUAGES } from "../../../constants/features";
import { HISTORICAL_DATA_FEATURE } from "../../../constants/historicalData";

type PackageFeature = {
  entity_id_field: string | null;
  entity_id_label: string | null;
  entity_id_help_text: string | null;
};

type UsePackageDetailsOptions = {
  onPackageChange?: (
    packageId: string,
    metadataQuantities: Record<string, number> | null,
    packageFeatures: PackageFeature[] | null,
    editorialCategoryOptions: Array<{ value: string | number; label: string }>,
  ) => void;
  editorialCategoryOptions?: Array<{ value: string | number; label: string }>;
};

/**
 * Manages package details including available sites, languages, and metadata configuration.
 */
export function usePackageDetails(
  initialPackageId: string | null | undefined,
  options: UsePackageDetailsOptions = {},
) {
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    initialPackageId ?? null,
  );
  const [availableSites, setAvailableSites] = useState<string[]>([]);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [metadataQuantities, setMetadataQuantities] = useState<Record<string, number> | null>(null);
  const [packageFeatures, setPackageFeatures] = useState<PackageFeature[] | null>(null);
  const [defaultSiteStructureId, setDefaultSiteStructureId] = useState<string | null>(null);
  const [includesHistoricalData, setIncludesHistoricalData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch package details when packageId changes (for edit mode initialization)
  useEffect(() => {
    const fetchPackageDetails = async () => {
      if (!initialPackageId) return;

      setIsLoading(true);
      try {
        const pkg = await packagesService.getById(initialPackageId);
        if (pkg) {
          setMetadataQuantities(pkg.metadataQuantities || null);
          setDefaultSiteStructureId(pkg.defaultSiteStructureId ?? null);
          setIncludesHistoricalData((pkg.featureCodes ?? []).includes(HISTORICAL_DATA_FEATURE));

          if (pkg.features) {
            const features = pkg.features.map(f => ({
              entity_id_field: f.entity_id_field,
              entity_id_label: f.entity_id_label,
              entity_id_help_text: f.entity_id_help_text,
            }));
            setPackageFeatures(features);

            const allSites = new Set<string>();
            const allLanguages = new Set<string>();

            for (const feature of pkg.features) {
              if (feature.available_sites === null) {
                SITES.forEach(site => allSites.add(site));
              } else {
                feature.available_sites?.forEach(site => allSites.add(site));
              }

              if (feature.available_languages === null) {
                LANGUAGES.forEach(lang => allLanguages.add(lang));
              } else {
                feature.available_languages?.forEach(lang => allLanguages.add(lang));
              }
            }

            setAvailableSites([...allSites]);
            setAvailableLanguages([...allLanguages]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch package details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPackageDetails();
  }, [initialPackageId]);

  const handlePackageChange = useCallback(
    async (packageId: string) => {
      setSelectedPackageId(packageId);
      setIsLoading(true);

      try {
        const pkg = await packagesService.getById(packageId);
        const quantities = pkg?.metadataQuantities || null;
        setMetadataQuantities(quantities);
        setDefaultSiteStructureId(pkg?.defaultSiteStructureId ?? null);
        setIncludesHistoricalData((pkg?.featureCodes ?? []).includes(HISTORICAL_DATA_FEATURE));

        let features: PackageFeature[] | null = null;
        if (pkg?.features) {
          features = pkg.features.map(f => ({
            entity_id_field: f.entity_id_field,
            entity_id_label: f.entity_id_label,
            entity_id_help_text: f.entity_id_help_text,
          }));
          setPackageFeatures(features);

          const allSites = new Set<string>();
          const allLanguages = new Set<string>();

          for (const feature of pkg.features) {
            if (feature.available_sites === null) {
              SITES.forEach(site => allSites.add(site));
            } else {
              feature.available_sites?.forEach(site => allSites.add(site));
            }

            if (feature.available_languages === null) {
              LANGUAGES.forEach(lang => allLanguages.add(lang));
            } else {
              feature.available_languages?.forEach(lang => allLanguages.add(lang));
            }
          }

          setAvailableSites([...allSites]);
          setAvailableLanguages([...allLanguages]);
        }

        options.onPackageChange?.(
          packageId,
          quantities,
          features,
          options.editorialCategoryOptions || [],
        );
      } catch (error) {
        console.error("Failed to fetch package features:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [options],
  );

  return {
    selectedPackageId,
    availableSites,
    availableLanguages,
    metadataQuantities,
    packageFeatures,
    defaultSiteStructureId,
    includesHistoricalData,
    isLoading,
    handlePackageChange,
  };
}
